import { COOKIE_CONSENT_SCHEMA_VERSION, COOKIE_CONSENT_STORAGE_KEY } from "@/lib/legal/cookie-consent-storage";

/**
 * Google Consent Mode v2, over the single yes/no the cookie notice asks.
 *
 * The notice has one category, and its text says the site is monitored to understand how it is
 * used: analytics. So "Aceitar" grants `analytics_storage` and nothing else; the three advertising
 * signals stay denied until there is a marketing category to ask about (PRIV-02). "Recusar" keeps
 * all four denied.
 *
 * Two pieces, both writing the `dataLayer` in the form GTM reads consent commands in (an
 * `arguments` object, as `gtag()` pushes it; a plain array or object is not a command):
 *
 * - {@link CONSENT_MODE_BOOTSTRAP}: a script the root layout writes into the page **before** the
 *   container loads, so the denied `default` — and, for whoever accepted on a past visit, the
 *   `update` — are queued ahead of `gtm.js` and no tag ever runs on an unset state;
 * - {@link sendConsentUpdate}: the banner's answer, given now.
 */

type ConsentSignal = "granted" | "denied";

type ConsentSignals = {
  ad_storage: ConsentSignal;
  analytics_storage: ConsentSignal;
  ad_user_data: ConsentSignal;
  ad_personalization: ConsentSignal;
};

/** Everything denied: what a visitor who has not answered (or refused) is treated as. */
export const CONSENT_DEFAULT: ConsentSignals = {
  ad_storage: "denied",
  analytics_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
};

/** Only the analytics signal moves on a yes (D-09): the advertising ones are not asked about. */
const CONSENT_ACCEPTED: Partial<ConsentSignals> = { analytics_storage: "granted" };

/**
 * The page's first script. Plain ES5 on purpose — it runs before anything else and must not depend
 * on the bundle — and it never throws: unreadable storage is an unanswered question, which is the
 * `default` already sent.
 *
 * It reads the record `cookie-consent.ts` writes, by the same key and version.
 */
export const CONSENT_MODE_BOOTSTRAP = `(function(w,l){
w[l]=w[l]||[];
function gtag(){w[l].push(arguments);}
gtag('consent','default',${JSON.stringify(CONSENT_DEFAULT)});
try{
var r=JSON.parse(w.localStorage.getItem(${JSON.stringify(COOKIE_CONSENT_STORAGE_KEY)}));
if(r&&r.v===${COOKIE_CONSENT_SCHEMA_VERSION}&&r.accepted===true){gtag('consent','update',${JSON.stringify(CONSENT_ACCEPTED)});}
}catch(e){}
})(window,'dataLayer');`;

/**
 * Tells the container what the visitor just answered. Best-effort like every other push: analytics
 * must never alter the interaction it comes from.
 */
export function sendConsentUpdate(accepted: boolean): void {
  if (typeof window === "undefined") return;

  try {
    const sink = Array.isArray(window.dataLayer) ? window.dataLayer : [];
    if (sink !== window.dataLayer) window.dataLayer = sink;

    // The container reads an `arguments` object, not an array: it is the shape `gtag()` pushes.
    (function gtag(..._command: unknown[]) {
      // eslint-disable-next-line prefer-rest-params
      sink.push(arguments as unknown as Record<string, unknown>);
    })("consent", "update", accepted ? CONSENT_ACCEPTED : CONSENT_DEFAULT);
  } catch {
    // The signal is lost; the next page's bootstrap sends the stored answer.
  }
}
